"use client";

import { AlertTriangle, Info, XCircle } from "lucide-react";
import type { ComponentProps } from "react";

import { cn } from "@/lib/utils/cn";

type NoticeVariant = "error" | "warning" | "info";

type NoticeProps = Omit<ComponentProps<"div">, "children" | "title"> & {
  title?: string;
  message: string;
  variant?: NoticeVariant;
};

const variantStyles: Record<
  NoticeVariant,
  { container: string; icon: string; title: string }
> = {
  error: {
    container:
      "border-destructive/30 bg-destructive/10",
    icon: "text-destructive",
    title: "text-destructive",
  },
  warning: {
    container:
      "border-warning/30 bg-warning/10",
    icon: "text-warning",
    title: "text-warning",
  },
  info: {
    container:
      "border-primary/30 bg-primary/10",
    icon: "text-primary",
    title: "text-primary",
  },
};

const variantIcons: Record<NoticeVariant, typeof Info> = {
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

export function Notice({
  title,
  message,
  variant = "info",
  className,
  ...props
}: NoticeProps) {
  const styles = variantStyles[variant] ?? variantStyles.info;
  const Icon = variantIcons[variant] ?? Info;

  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-lg border p-3",
        styles.container,
        className,
      )}
      {...props}
    >
      <Icon className={cn("mt-0.5 size-4 shrink-0", styles.icon)} />
      <div className="flex min-w-0 flex-col gap-0.5">
        {title && (
          <span className={cn("text-sm font-semibold", styles.title)}>
            {title}
          </span>
        )}
        <span className="text-sm text-foreground/80">{message}</span>
      </div>
    </div>
  );
}
